import {CARD_CURRENCY_PARAM} from 'modules/offerCheck/contants'
import {CommonErrorPayloadFormatType} from 'types/errorResponse'

import {userSuccessfullyAuthenticated} from '@vio/core/src/auth/modules/actions'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'

import {ModuleActionType} from './actions'
import {CLEAR_ERROR, ERROR, ErrorActionType} from './actions/error'
import {FATAL_ERROR} from './actions/fatalError'
import {
  clearOfferCheckOverrideUrl,
  setOfferCheckOverrideUrl
} from './actions/offerCheckOverride'
import {
  PayrailsStatus,
  setEnabledWallets,
  setPayrailsStatus,
  setPspInitialized
} from './actions/psp'
import {setIsEmbeddedWithKiwi} from './actions/runtime'

import type {PaymentMethodConfiguration} from 'services/payment/paymentMethods'

export type CommonStateType = {
  error: CommonErrorPayloadFormatType | null | undefined
  moduleState?: string
  httpRequestId?: string
  offerCheckOverrideUrl?: string
  // Whether last offer check call was triggered by refresh
  isOfferCheckRefetch?: boolean
  // should be removed with https://app.shortcut.com/findhotel/story/176126
  isSubmitting?: boolean
  // Whether Stripe initialization is complete
  isPspInitialized: boolean
  // Payrails SDK setup outcome for the card form
  payrailsStatus: PayrailsStatus
  // PSP decision on wallet availability
  enabledWallets: PaymentMethodConfiguration[] | null
  // Whether we run inside an embedded flow of a partner brand
  isEmbeddedWithKiwi: boolean
}

export const initialState: CommonStateType = {
  error: null,
  offerCheckOverrideUrl: undefined,
  isOfferCheckRefetch: false,
  isSubmitting: false,
  isPspInitialized: false,
  payrailsStatus: 'idle',
  enabledWallets: null,
  isEmbeddedWithKiwi: false
}

const reducer = (
  state: CommonStateType = initialState,
  action: ModuleActionType
): CommonStateType => {
  if (offerCheckApi.endpoints.getOfferCheck.matchPending(action)) {
    const isMainOfferCheck = !action.meta.arg.originalArgs.url.includes(
      `${CARD_CURRENCY_PARAM}=`
    )

    if (isMainOfferCheck) {
      return {
        ...state,
        isOfferCheckRefetch: Boolean(action.meta.arg.forceRefetch)
      }
    }
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchFulfilled(action)) {
    const {httpRequestId} = action.payload

    return {
      ...state,
      httpRequestId
    }
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchRejected(action)) {
    const requestId = action?.payload?.headers?.['x-request-id']

    return requestId ? {...state, httpRequestId: requestId} : state
  }

  if (bookingRetrieveApi.endpoints.getBookingById.matchFulfilled(action)) {
    return {
      ...state,
      httpRequestId: action.payload?.httpRequestId
    }
  }
  if (bookingRetrieveApi.endpoints.getBookingById.matchRejected(action)) {
    return {
      ...state,
      httpRequestId: action?.payload?.headers?.['x-request-id']
    }
  }
  if (setPspInitialized.match(action)) {
    return {
      ...state,
      isPspInitialized: action.payload
    }
  }
  if (setPayrailsStatus.match(action)) {
    return {
      ...state,
      payrailsStatus: action.payload
    }
  }
  if (setEnabledWallets.match(action)) {
    return {
      ...state,
      enabledWallets: action.payload
    }
  }
  if (setIsEmbeddedWithKiwi.match(action)) {
    return {
      ...state,
      isEmbeddedWithKiwi: action.payload
    }
  }
  switch (action.type) {
    case FATAL_ERROR:
    case ERROR: {
      return {
        ...state,
        error: (action as ErrorActionType).error,
        moduleState: action.type
      }
    }

    case CLEAR_ERROR: {
      return {
        ...state,
        error: null,
        moduleState: action.type
      }
    }

    case userSuccessfullyAuthenticated.type: {
      return {
        ...state,
        moduleState: action.type
      }
    }

    case setOfferCheckOverrideUrl.type: {
      const overrideUrl = (
        action as ReturnType<typeof setOfferCheckOverrideUrl>
      ).payload

      return {
        ...state,
        offerCheckOverrideUrl: overrideUrl,
        moduleState: action.type
      }
    }

    case clearOfferCheckOverrideUrl.type: {
      return {
        ...state,
        offerCheckOverrideUrl: undefined,
        moduleState: action.type
      }
    }

    default: {
      return state
    }
  }
}

export default reducer
